import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';

/**
 * Module 1 — A1.8 "Translate into actions" sheet, pixel-faithful to mockup
 * `module1-performance.jsx` M1_S8_TranslateActions:
 *   - Source card (magenta) — the strategic-focus gap
 *   - 3 destination cards (Module 2 / Module 6 / Module 4) that route on tap
 *
 * Bottom sheet on mobile, centered card on desktop. Data-independent (routing
 * flow only) — no pipeline dependency.
 *
 * Props:
 *   - open, onClose
 *   - metricLabel, sourceText (e.g. "29% of target")
 *   - gapMetric (raw metric key, forwarded as ?gap=)
 */
const TranslateActionsSheet = ({ open, onClose, metricLabel, sourceText, gapMetric }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  if (!open) return null;

  const gap = encodeURIComponent(gapMetric ?? '');
  const go = (path) => {
    onClose?.();
    navigate(path);
  };

  const destinations = [
    {
      mod: t('tcn.m1.translate.m2.mod', 'Module 2'),
      title: t('tcn.m1.translate.m2.title', 'Open at-risk new creators'),
      sub: t('tcn.m1.translate.m2.sub', 'Creators flagged on this gap'),
      icon: 'people', color: TCN.cyan, onClick: () => go(`/tcn/me/new-creators?gap=${gap}`),
    },
    {
      mod: t('tcn.m1.translate.m6.mod', 'Module 6'),
      title: t('tcn.m1.translate.m6.title', 'Add to Action Log'),
      sub: t('tcn.m1.translate.m6.sub', 'Coaching topic + 48h goal'),
      icon: 'check', color: TCN.green, onClick: () => go('/tcn/me/actions'),
    },
    {
      mod: t('tcn.m1.translate.m4.mod', 'Module 4'),
      title: t('tcn.m1.translate.m4.title', 'Filter creators by alert'),
      sub: t('tcn.m1.translate.m4.sub', 'See individual MTD pace'),
      icon: 'filter', color: TCN.amber, onClick: () => go(`/tcn/me/creators?gap=${gap}`),
    },
  ];

  // Portal to <body> so the fixed overlay escapes any ancestor that creates a
  // stacking/containing context (transform / backdrop-filter on page cards),
  // otherwise the TabBar (z-50, sibling of AppShell) paints over the sheet.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520, background: TCN.surface1,
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
          borderRadius: 'min(20px, 20px)', border: `1px solid ${TCN.border}`,
          padding: '16px 16px calc(env(safe-area-inset-bottom, 0px) + 20px)',
          maxHeight: '85dvh', overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16 }}>
          <button
            type="button" onClick={onClose} aria-label={t('tcn.common.close', 'Close')}
            style={{ background: 'transparent', border: 'none', color: TCN.textDim, cursor: 'pointer', padding: 2 }}
          >
            <Icon name="close" size={22} />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: TCN.text }}>
              {t('tcn.m1.translate.title', 'Translate into actions')}
            </div>
            <div style={{ fontSize: 12, color: TCN.textDim, marginTop: 2 }}>
              {t('tcn.m1.translate.subtitle', 'Pick where to act on {{metric}}', { metric: metricLabel })}
            </div>
          </div>
        </div>

        {/* Source card */}
        <div
          style={{
            padding: 14, background: `${TCN.magenta}14`, border: `1px solid ${TCN.magenta}44`,
            borderRadius: 12, marginBottom: 16,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: TCN.magenta, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {t('tcn.m1.translate.source', 'Source')}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: TCN.text, marginTop: 4 }}>
            {metricLabel}{sourceText ? ` (${sourceText})` : ''}
          </div>
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, color: TCN.textMute, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8 }}>
          {t('tcn.m1.translate.destinations', 'Suggested next destinations')}
        </div>

        {destinations.map((d, i) => (
          <button
            key={i}
            type="button"
            onClick={d.onClick}
            style={{
              width: '100%', textAlign: 'start', cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 12, padding: 14,
              background: TCN.surface2, border: `1px solid ${TCN.border}`, borderRadius: 14, marginBottom: 8,
            }}
          >
            <div style={{ width: 38, height: 38, borderRadius: 10, background: `${d.color}1f`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name={d.icon} size={18} color={d.color} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, color: TCN.textMute, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{d.mod}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: TCN.text, marginTop: 2 }}>{d.title}</div>
              <div style={{ fontSize: 12, color: TCN.textDim, marginTop: 2 }}>{d.sub}</div>
            </div>
            <Icon name="chevron" size={16} color={TCN.textMute} />
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
};

export default TranslateActionsSheet;
